import React from 'react';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import FormattedCurrency from '../../../../../components/currency/FormattedCurrency';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { bandPenetration, isOutOfBand } from '../logic';
import { PayBand } from '../types';

type Props = {
  payBand: PayBand | null;
  amount: number;
  currency: string;
};

// Informational pay-band indicator: a min–max bar with a marker at the proposed
// amount. Out-of-band proposals are highlighted but never block submission.
export const PayBandCell: React.FC<Props> = ({ payBand, amount, currency }) => {
  const t = useNamespacedTranslation('compensationReviews.table');

  if (!payBand) {
    return (
      <div className="compensation-pay-band">
        <div
          className="compensation-pay-band__empty"
          title={t('noPayBand')}
          aria-label={t('noPayBand')}
        />
      </div>
    );
  }

  const outOfBand = isOutOfBand(amount, payBand);
  const markerLeft = `${bandPenetration(amount, payBand)}%`;

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`pay-band-${payBand.min}-${payBand.max}`}>
          <FormattedCurrency value={payBand.min} currency={currency} />
          {' – '}
          <FormattedCurrency value={payBand.max} currency={currency} />
          {outOfBand ? ` · ${t('outOfBand')}` : ''}
        </Tooltip>
      }>
      <div className="compensation-pay-band">
        <div
          className={classNames('compensation-pay-band__track', {
            'compensation-pay-band__track--out-of-band': outOfBand,
          })}>
          <div
            className={classNames('compensation-pay-band__marker', {
              'compensation-pay-band__marker--out-of-band': outOfBand,
            })}
            style={{ left: markerLeft }}
          />
        </div>
        <div className="d-flex justify-content-between text-muted small mt-1">
          <span>
            <FormattedCurrency value={payBand.min} currency={currency} />
          </span>
          <span>
            <FormattedCurrency value={payBand.max} currency={currency} />
          </span>
        </div>
      </div>
    </OverlayTrigger>
  );
};
